import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { Auth0Provider } from '@auth0/auth0-react';
import { LicenseInfo } from '@mui/x-license-pro';

// local storageのデータをreduxで管理する
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';

// スクロールバーのカスタマイズ。index.jsではcssのみ適用する
// https://github.com/goldenyz/react-perfect-scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css';

// プログレスバー。index.jsではcssのみ適用する
// https://www.npmjs.com/package/nprogress
import 'nprogress/nprogress.css';

// react-quillで作成したコンテンツを表示する際に利用する
import 'react-quill/dist/quill.snow.css';

import App from './App';
import reportWebVitals from './reportWebVitals';
import store, { history } from 'src/store';
import { SettingsProvider } from 'src/contexts/SettingsContext';
import { UserProvider } from 'src/contexts/UserContext';
import { basename, auth0Config, muiConfig, datadogConfig } from 'src/config';
import { APP_NAME } from 'src/constants';

// 開発時のみ利用する
//import 'src/__mocks__';

LicenseInfo.setLicenseKey(muiConfig.license);

const setFavicon = () => {
  const dcIdentifier = process.env.REACT_APP_DC_IDENTIFIER;
  const faviconLink =
    document.querySelector("link[rel*='icon']") ||
    document.createElement('link');
  faviconLink.type = 'image/x-icon';
  faviconLink.rel = 'icon';

  if (dcIdentifier === 'shin-otemachi') {
    faviconLink.href = `${process.env.PUBLIC_URL}/favicon-shin-otemachi.ico`;
  } else {
    faviconLink.href = `${process.env.PUBLIC_URL}/favicon-otemachi.ico`;
  }

  document.getElementsByTagName('head')[0].appendChild(faviconLink);
};

const setTitle = () => {
  document.title = APP_NAME;
};

setFavicon();
setTitle();

if (process.env.REACT_APP_DATADOG_ENABLED === 'true') {
  const { datadogRum } = require('@datadog/browser-rum');
  const { datadogLogs } = require('@datadog/browser-logs');

  datadogRum.init({
    applicationId: datadogConfig.applicationId,
    clientToken: datadogConfig.clientToken,
    site: 'datadoghq.com',

    service: datadogConfig.service,
    env: datadogConfig.env,
    version: datadogConfig.version,

    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',

    allowedTracingUrls: [
      'http://localhost',
      /https:\/\/app(\.dev)?\.portal\.bbtower\.com\/(otemachi|shin-otemachi)\/api/
    ],
    traceSampleRate: 100
  });

  datadogLogs.init({
    clientToken: datadogConfig.clientToken,
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    sampleRate: 100,
    service: datadogConfig.service,
    env: datadogConfig.env,
    version: datadogConfig.version
  });

  datadogRum.startSessionReplayRecording();
}

const container = document.getElementById('root');
const root = createRoot(container);

const onRedirectCallback = (appState) => {
  history.push(
    appState && appState.returnTo
      ? appState.returnTo.replace(basename, '')
      : window.location.origin
  );
};

const persistor = persistStore(store);

root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <SettingsProvider>
        <ConnectedRouter history={history} basename={history.basename}>
          <Auth0Provider
            {...auth0Config}
            redirectUri={`${window.location.origin}${basename}/`}
            onRedirectCallback={onRedirectCallback}
          >
            <UserProvider>
              <App />
            </UserProvider>
          </Auth0Provider>
        </ConnectedRouter>
      </SettingsProvider>
    </PersistGate>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
